import { BeangoError } from './beango-error';
import { ErrorCode, type ErrorCodeValue } from './error-codes';

const API_URL: string = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

let currentOrganizationId: number | null = null;

export function setOrganizationId(orgId: number | null) {
  currentOrganizationId = orgId;
}

export function getOrganizationId(): number | null {
  return currentOrganizationId;
}

export interface ApiError {
  message: string;
  detail?: string;
  status?: number;
}

export interface RequestOptions {
  errorCode?: ErrorCodeValue;
  token: string; // Required — explicit auth token
}

export class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_URL) {
    this.baseUrl = baseUrl;
  }

  private getOrganizationId(): string | null {
    if (currentOrganizationId) return String(currentOrganizationId);
    return localStorage.getItem('currentOrganizationId');
  }

  private buildHeaders(token: string): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    headers.Authorization = `Bearer ${token}`;

    const orgId = this.getOrganizationId();
    if (orgId) {
      headers['X-Organization-ID'] = orgId;
    }

    return headers;
  }

  private async handleError(response: Response, errorCode: ErrorCodeValue): Promise<never> {
    let message = 'An error occurred';
    let detail: string | undefined;

    try {
      const data = (await response.json()) as { message?: string; detail?: string };
      message = data.message ?? data.detail ?? response.statusText;
      detail = data.detail;
    } catch {
      message = response.statusText;
    }

    throw new BeangoError(errorCode, response.status, message, detail);
  }

  private buildUrl(path: string): string {
    if (path.startsWith('http')) return path;
    return `${this.baseUrl}${path}`;
  }

  async request<T>(
    method: string,
    path: string,
    body?: unknown,
    opts?: RequestOptions,
  ): Promise<T> {
    const errorCode = opts?.errorCode ?? ErrorCode.UNKNOWN;
    const token = opts?.token;

    if (!token) {
      throw new BeangoError(errorCode, 0, 'Authentication token required — pass token explicitly');
    }

    const headers = this.buildHeaders(token);

    const fetchOptions: RequestInit = {
      method,
      headers,
    };

    if (body !== undefined && method !== 'GET') {
      fetchOptions.body = JSON.stringify(body);
    }

    const response = await fetch(this.buildUrl(path), fetchOptions);

    if (!response.ok) {
      await this.handleError(response, errorCode);
    }

    if (response.status === 204) {
      return {} as T;
    }

    return response.json() as Promise<T>;
  }

  async get<T>(path: string, opts: RequestOptions): Promise<T> {
    return this.request<T>('GET', path, undefined, opts);
  }

  async post<T>(path: string, data: unknown, opts: RequestOptions): Promise<T> {
    return this.request<T>('POST', path, data, opts);
  }

  async put<T>(path: string, data: unknown, opts: RequestOptions): Promise<T> {
    return this.request<T>('PUT', path, data, opts);
  }

  async patch<T>(path: string, data: unknown, opts: RequestOptions): Promise<T> {
    return this.request<T>('PATCH', path, data, opts);
  }

  async delete<T>(path: string, opts: RequestOptions): Promise<T> {
    return this.request<T>('DELETE', path, undefined, opts);
  }
}

export const api = new ApiClient();
